import { useCallback } from "react";
import { getErrorMessages, useGetAccountQuery } from "../../../../services";
import type { AccountDetails } from "../../../../services";

export interface UseAccountDataReturn {
  account: AccountDetails | null;
  isLoading: boolean;
  error: string | null;
  notFoundError: boolean;
  refetchAccount: () => Promise<void>;
}

export const useAccountData = (accountId: number): UseAccountDataReturn => {
  const { data, isLoading, error, refetch } = useGetAccountQuery(accountId);

  // The interceptor rejects with the normalized `{ errors: [...] }` shape, so
  // the RTK error is consumed directly by `getErrorMessages`. Toasting is
  // handled globally by `errorToastMiddleware` — no `notify.apiError` here.
  const errorMessages = error
    ? getErrorMessages(error as Parameters<typeof getErrorMessages>[0])
    : [];
  const notFoundError = errorMessages[0] === "Account not found";
  const errorMessage = error
    ? (errorMessages[0] ?? "Failed to load account data")
    : null;

  const refetchAccount = useCallback(async () => {
    await refetch();
  }, [refetch]);

  return {
    account: data ?? null,
    isLoading,
    error: errorMessage,
    notFoundError,
    refetchAccount,
  };
};
